if (!customElements.get("arc-compatibility-filtering")) {
  class ARCCompatibilityFiltering extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.button = this.querySelector('button[type="submit"]');
      this.brandSelect = this.querySelector("select.filter-brand");
      this.modelSelect = this.querySelector("select.filter-model");
      this.yearSelect = this.querySelector("select.filter-year");

      this.compatibilityData = {};
      this.isLoading = false;
      this.storefrontAccessToken =
        this.dataset.storefrontAccessToken ||
        "817182a77a624257b6d8fbcbfbdbeb2f";

      this.init();
    }

    async init() {
      this.setupEventListeners();
      await this.fetchCompatibilityData();
      this.populateBrands();
    }

    setupEventListeners() {
      this.form.addEventListener("submit", this.onSubmit.bind(this));
      this.brandSelect?.addEventListener(
        "change",
        this.onBrandChange.bind(this),
      );
      this.modelSelect?.addEventListener(
        "change",
        this.onModelChange.bind(this),
      );
      this.yearSelect?.addEventListener("change", this.onYearChange.bind(this));
    }

    async fetchCompatibilityData() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.toggleLoading(true);

      try {
        const allProducts = await this.fetchAllProducts();
        this.compatibilityData = this.buildCompatibilityObject(allProducts);
      } catch (error) {
        console.error("Error fetching compatibility data:", error);
      } finally {
        this.isLoading = false;
        this.toggleLoading(false);
      }
    }

    async fetchAllProducts() {
      const products = [];
      let hasNextPage = true;
      let cursor = null;

      const query = `
        query GetProductsWithCompatibility($cursor: String) {
          products(first: 250, after: $cursor) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                fitment: metafield(namespace: "fitment", key: "json") {
                  value
                  type
                }
              }
            }
          }
        }
      `;

      while (hasNextPage) {
        const response = await fetch(
          window.location.origin + "/api/2024-01/graphql.json",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token":
                this.storefrontAccessToken,
            },
            body: JSON.stringify({
              query,
              variables: { cursor },
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.errors) {
          throw new Error(data.errors[0].message);
        }

        const productData = data.data.products;
        products.push(...productData.edges);

        hasNextPage = productData.pageInfo.hasNextPage;
        cursor = productData.pageInfo.endCursor;
      }

      return products;
    }

    buildCompatibilityObject(products) {
      const compatibility = {};

      products.forEach(({ node: product }) => {
        if (!product.fitment || !product.fitment.value) return;

        try {
          const fitmentArray = JSON.parse(product.fitment.value);

          fitmentArray.forEach((brandItem) => {
            const brand = brandItem.brand;
            if (!brand) return;
            if (!brandItem.models || !Array.isArray(brandItem.models)) return;

            brandItem.models.forEach((modelItem) => {
              const model = modelItem.model;
              const years = modelItem.years || [];

              if (!model) return;

              // Initialize brand if it doesn't exist
              if (!compatibility[brand]) {
                compatibility[brand] = {};
              }

              // Initialize model if it doesn't exist
              if (!compatibility[brand][model]) {
                compatibility[brand][model] = new Set();
              }

              // Add years to the model
              years.forEach((year) => {
                compatibility[brand][model].add(year);
              });
            });
          });
        } catch (error) {
          console.error(
            `Error parsing fitment for product ${product.title}:`,
            error,
          );
        }
      });

      // Convert Sets to sorted arrays
      Object.keys(compatibility).forEach((brand) => {
        Object.keys(compatibility[brand]).forEach((model) => {
          compatibility[brand][model] = Array.from(
            compatibility[brand][model],
          ).sort((a, b) => b - a);
        });
      });

      return compatibility;
    }

    populateBrands() {
      if (!this.brandSelect) return;

      // Clear existing options except the default
      this.clearSelect(this.brandSelect);

      // Get sorted brand list
      const brands = Object.keys(this.compatibilityData).sort();

      // Add brand options
      brands.forEach((brand) => {
        const option = document.createElement("option");
        option.value = brand;
        option.textContent = brand;
        this.brandSelect.appendChild(option);
      });

      // Enable the brand select
      this.brandSelect.disabled = false;
      this.brandSelect.closest(".form-group")?.classList.remove("disabled");
    }

    onBrandChange(event) {
      const selectedBrand = event.target.value;

      // Clear and disable model and year selects
      this.clearSelect(this.modelSelect);
      this.clearSelect(this.yearSelect);
      this.modelSelect.disabled = true;
      this.yearSelect.disabled = true;
      this.modelSelect.closest(".form-group")?.classList.add("disabled");
      this.yearSelect.closest(".form-group")?.classList.add("disabled");
      this.button.disabled = true;

      if (!selectedBrand) return;

      // Populate models for selected brand
      const models = Object.keys(
        this.compatibilityData[selectedBrand] || {},
      ).sort();

      models.forEach((model) => {
        const option = document.createElement("option");
        option.value = model;
        option.textContent = model;
        this.modelSelect.appendChild(option);
      });

      // Enable model select
      this.modelSelect.disabled = false;
      this.modelSelect.closest(".form-group")?.classList.remove("disabled");
    }

    onModelChange(event) {
      const selectedBrand = this.brandSelect.value;
      const selectedModel = event.target.value;

      // Clear and disable year select
      this.clearSelect(this.yearSelect);
      this.yearSelect.disabled = true;
      this.yearSelect.closest(".form-group")?.classList.add("disabled");
      this.button.disabled = true;

      if (!selectedModel) return;

      // Populate years for selected model
      const years =
        this.compatibilityData[selectedBrand]?.[selectedModel] || [];

      years.forEach((year) => {
        const option = document.createElement("option");
        option.value = year;
        option.textContent = year;
        this.yearSelect.appendChild(option);
      });

      // Enable year select
      this.yearSelect.disabled = false;
      this.yearSelect.closest(".form-group")?.classList.remove("disabled");
    }

    onYearChange(event) {
      const selectedYear = event.target.value;

      if (selectedYear) {
        this.button.disabled = false;
      } else {
        this.button.disabled = true;
      }
    }

    onSubmit(event) {
      event.preventDefault();

      const brand = this.brandSelect.value;
      const model = this.modelSelect.value;
      const year = this.yearSelect.value;

      if (!brand || !model || !year) {
        return;
      }

      // Construct the filter URL
      const url = new URL(window.location.origin + "/collections/all");
      url.searchParams.set("filter.p.m.fitment.make", brand);
      url.searchParams.set("filter.p.m.fitment.model", model);
      url.searchParams.set("filter.p.m.fitment.year", year);

      // Show loading state
      this.toggleLoading(true);

      // Navigate to the filtered collection
      window.location.href = url.toString();
    }

    clearSelect(select) {
      if (!select) return;

      // Remove all options except the default one
      const options = select.querySelectorAll("option:not(.default)");
      options.forEach((option) => option.remove());

      // Reset to default option
      const defaultOption = select.querySelector("option.default");
      if (defaultOption) {
        defaultOption.selected = true;
      }
    }

    toggleLoading(isLoading = true) {
      if (isLoading) {
        this.classList.add("is-loading");
        this.button?.classList.add("loading");
      } else {
        this.classList.remove("is-loading");
        this.button?.classList.remove("loading");
      }
    }
  }

  customElements.define(
    "arc-compatibility-filtering",
    ARCCompatibilityFiltering,
  );
}
